import { useState, useEffect } from "react";
import { Loader2, Trash2, AlertTriangle, Mail, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import api from "../../lib/api";

/**
 * Two-step permanent deletion:
 *  Step 1 → Request OTP (email). Backend stores hashed OTP + TTL.
 *  Step 2 → User types 6-digit code + ticks "confirmo" + OK → cascade delete.
 */
export default function DeleteOTPDialog({ open, onOpenChange, restaurant, onDone }) {
  const [step, setStep] = useState("intro");  // intro | code | deleting
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [sentTo, setSentTo] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep("intro");
      setOtp("");
      setSentTo(null);
      setExpiresAt(null);
      setConfirmed(false);
    }
  }, [open]);

  const requestOtp = async () => {
    if (!restaurant) return;
    setSendingOtp(true);
    try {
      const res = await api.requestDeleteOTP(restaurant.restaurant_id);
      setSentTo(res.sent_to || null);
      setExpiresAt(res.expires_at || null);
      // Dev fallback: if backend returned debug_otp (Resend not configured),
      // toast it so the user/tester can proceed without email delivery.
      if (res.debug_otp) {
        toast(`Código (dev): ${res.debug_otp}`, { duration: 20000 });
      }
      setStep("code");
    } catch (err) {
      toast.error(err.message || "Error al solicitar el código");
    } finally {
      setSendingOtp(false);
    }
  };

  const confirmDelete = async () => {
    if (!/^\d{6}$/.test(otp)) {
      toast.error("El código debe tener 6 dígitos");
      return;
    }
    if (!confirmed) {
      toast.error("Marca la casilla de confirmación");
      return;
    }
    setSubmitting(true);
    setStep("deleting");
    try {
      await api.confirmDeleteRestaurant(restaurant.restaurant_id, otp);
      toast.success(`${restaurant.name} eliminado definitivamente`);
      onDone?.(restaurant);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Código incorrecto o expirado");
      setStep("code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" />
            Borrar {restaurant?.name} definitivamente
          </DialogTitle>
          <DialogDescription>
            Esta acción <strong>no se puede deshacer</strong>. Para confirmar te enviaremos un código por email.
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="space-y-4">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Se eliminarán todos los datos del restaurante</strong>, incluyendo ventas, clientes, inventario, menús, recetas y facturación. Si aún no lo has hecho, <strong>exporta una copia antes</strong>.
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button
                onClick={requestOtp}
                disabled={sendingOtp}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="request-otp-btn"
              >
                {sendingOtp
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Enviando…</>
                  : <><Mail className="h-4 w-4 mr-1.5" /> Enviarme el código</>}
              </Button>
            </div>
          </div>
        )}

        {(step === "code" || step === "deleting") && (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex gap-2">
              <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                {sentTo ? <>Código enviado a <strong>{sentTo}</strong>. Revisa tu bandeja y la de spam.</> : "Código generado. Revisa tu email."}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Código de 6 dígitos</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                autoFocus
                maxLength={6}
                inputMode="numeric"
                className="font-mono text-lg tracking-widest text-center"
                data-testid="otp-input"
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(!!v)}
                className="mt-0.5"
                data-testid="confirm-checkbox"
              />
              <span>
                He descargado los datos que necesitaba y entiendo que esto eliminará <strong>{restaurant?.name}</strong> de forma permanente.
              </span>
            </label>

            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={requestOtp} disabled={sendingOtp || submitting}>
                {sendingOtp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reenviar código"}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
                <Button
                  onClick={confirmDelete}
                  disabled={submitting || !confirmed || otp.length !== 6}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Borrar definitivamente"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
